import { create } from "zustand";
import {
  ICustomField,
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { devtools } from "zustand/middleware";
import { IdleManager } from "tools/idle";
import { registerStore } from "store/utils/reset-store.utils";
import {
  createRequestSeq,
  languageCodeQuery,
  resolveCacheLanguage,
} from "store/utils/localised-cache.utils";

type State = {
  // key - show id, value array of new stands
  items: Record<string, Array<ICustomField>>;
  language: Record<string, string>;
  isLoading: boolean;
};

type Action = {
  fetch: (
    showId: string,
    force?: boolean,
    languageCode?: string
  ) => Promise<void>;
  update: (showId: string, languageCode?: string) => Promise<void>;
  autoCatch: (showId: string) => Promise<void>;
  getByShowId: (showId: string) => Array<ICustomField>;
  getAll: () => Record<string, Array<ICustomField>>;
  getByShowIdAndCustomField: (
    showId: string,
    customFieldId: string
  ) => ICustomField | null;
};

/*
 * One shared list, not a fresh `[]` per call: this selector runs inside
 * `useShowCustomFields`, so a new reference re-rendered every subscriber.
 */
const EMPTY_CUSTOM_FIELDS = (Object.freeze([]) as unknown) as ICustomField[];

const nextRequest = createRequestSeq();

export const useCustomFields = create(
  devtools<State & Action>((set, get) => ({
    items: {},
    language: {},
    isLoading: false,

    getAll: () => {
      const { items } = get();

      return items;
    },

    getByShowId: (showId: string): Array<ICustomField> =>
      get().items?.[showId] ?? EMPTY_CUSTOM_FIELDS,

    getByShowIdAndCustomField: (
      showId: string,
      customFieldId: string
    ): ICustomField | null => {
      const { items } = get();

      if (!items?.[showId]) {
        return null;
      }

      const newStand = items[showId].find(
        (newStand) => newStand.id === customFieldId
      );

      if (!newStand) {
        return null;
      }

      return newStand;
    },

    autoCatch: async (showId: string) => {
      const { update } = get();

      IdleManager.getInstance().addSubscriber(
        `custom-field-${showId}`,
        async () => {
          await update(showId);
        }
      );

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.CREATED_CUSTOM_FIELD,
        listenerId: "CUSTOM_FIELD_STORE",
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.UPDATED_CUSTOM_FIELD,
        listenerId: "CUSTOM_FIELD_STORE",
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.DELETED_CUSTOM_FIELD,
        listenerId: "CUSTOM_FIELD_STORE",
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.UNLINKED_TASK_TO_CUSTOM_FIELD,
        listenerId: "CUSTOM_FIELD_STORE",
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.LINKED_TASK_TO_CUSTOM_FIELD,
        listenerId: "CUSTOM_FIELD_STORE",
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });
    },

    update: async (showId: string, languageCode?: string) => {
      const { items, language } = get();
      const nextLanguage = resolveCacheLanguage(language, showId, languageCode);
      const isLatestRequest = nextRequest(showId);

      try {
        const customFields = await apiV2.customField
          .getAll({
            showId,
            ...languageCodeQuery(nextLanguage),
          })
          .catch(() => []);

        if (!isLatestRequest()) {
          return;
        }

        set(() => ({
          items: {
            ...items,

            [showId]: [...customFields],
          },
          language: { ...language, [showId]: nextLanguage },
        }));
      } catch (e) {
        console.error("custom field store:", e);
      }
    },

    fetch: async (showId: string, force = false, languageCode?: string) => {
      const { language, update } = get();
      const nextLanguage = resolveCacheLanguage(language, showId, languageCode);

      if (!force && language[showId] === nextLanguage) {
        if (get().items[showId] || get().isLoading) {
          return;
        }
      }

      set(() => ({ isLoading: true }));

      try {
        await update(showId, nextLanguage);
      } catch (e) {
        console.error(e);
      } finally {
        set(() => ({ isLoading: false }));
      }
    },
  }))
);

registerStore(useCustomFields);
